import "instantsearch.css/themes/satellite.css";
import { history } from "instantsearch.js/es/lib/routers";
import { useEffect } from "react";
import {
  CurrentRefinements,
  HierarchicalMenu,
  InfiniteHits,
  InstantSearch,
  RangeInput,
  RefinementList,
  SearchBox,
  useInstantSearch,
} from "react-instantsearch";
import { useSearch } from "../state";
import "./Facet.css";
import { Header, HitMapping, useMode } from "./Header";

export const Block = ({ title, children }: { title: string; children?: React.ReactNode }) => (
  <>
    <h2 className="text-2xl font-semibold">{title}</h2>
    {children}
  </>
);

export const Loading = () => {
  const { status } = useInstantSearch();
  if (!["loading", "stalled"].includes(status)) return null;
  return (
    <div className="h-screen bg-white">
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    </div>
  );
};

const MapResults = () => {
  const setVisible = useSearch((s) => s.setVisible);

  useEffect(() => {
    setVisible(true);
    return () => setVisible(false);
  }, [setVisible]);

  return null;
};

const FacetContent = ({
  facetClass = "sm:w-[20rem] lg:w-[23rem]",
  className = "pl-10 sm:pl-20",
  children,
}: FacetProps) => {
  const { mode = "grid" } = useMode();
  // @ts-ignore
  const [Hit, hitStyle] = HitMapping[mode] || HitMapping["grid"];
  const map = mode === "map";

  return (
    <div
      className={`${className} fixed mt-14 flex z-10 h-screen bg-white${map ? "" : " w-screen"}`}
    >
      <div className={`${facetClass} rounded-t-2xl flex-none overflow-y-scroll p-2`}>
        <Header />
        {children}
        <div className="py-7" />
      </div>
      <div className="flex-grow z-9 bg-sidebar-menu overflow-y-scroll w-0">
        {/*<Breadcrumb attributes={["zone_1", "zone_2"]} />*/}
        <CurrentRefinements className="p-2 fixed bg-sidebar-menu" />
        <Loading />
        {map ? (
          <MapResults />
        ) : (
          <InfiniteHits
            showPrevious={false}
            hitComponent={Hit}
            className="pt-10"
            classNames={{ item: hitStyle }}
            translations={{
              showMoreButtonText: "Meer resultaten",
            }}
          />
        )}
      </div>
    </div>
  );
};

// https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-future
const future = { preserveSharedStateOnUnmount: true };
const hist = history({ cleanUrlOnDispose: true });

interface FacetProps {
  facetClass?: string;
  className?: string;
  children: React.ReactNode;
}

export const BaseFacet = ({ children, ...props }: FacetProps) => {
  const searchClient = useSearch((s) => s.client);

  return (
    <InstantSearch
      indexName="reference"
      searchClient={searchClient}
      future={future}
      routing={{ router: hist }}
    >
      <FacetContent {...props}>{children}</FacetContent>
    </InstantSearch>
  );
};

const rangeOptions = {
  translations: { separatorElementText: "-", submitButtonText: "Ga" },
};
const bClass = "p-2";
export const Facet = () => (
  <BaseFacet>
    <div className="py-2">
      <SearchBox placeholder="Zoek op adres / kadastraal nummer" />
    </div>
    <Block title="Basisgegevens">
      <div className={bClass}>
        <p>Provincie/gemeente</p>
        <HierarchicalMenu attributes={["zone_1", "zone_2"]} limit={50} sortBy={["name"]} />
      </div>
      <div className={bClass}>
        <p>Oppervlakte van perceel</p>
        <RangeInput attribute="area" {...rangeOptions} />
      </div>
      <div className={bClass}>
        <p>Oppervlakte bebouwd</p>
        <RangeInput attribute="building_area" {...rangeOptions} />
      </div>
      <div className={bClass}>
        <p>Bouwjaar</p>
        <RangeInput attribute="building_year" {...rangeOptions} />
      </div>
      <div className={bClass}>
        <p>Grondgebruik</p>
        <RefinementList attribute="land_cover" />
      </div>
      <div className={bClass}>
        <p>Enkelbestemming</p>
        <RefinementList attribute="zoning_single" />
      </div>
    </Block>
    {/*<Block title="Omgeving"></Block>*/}
    <Block title="Milieu, ecologie en bodem">
      <div className={bClass}>
        <p>Grondsoorten</p>
        <RefinementList attribute="soil" />
      </div>
      <div className={bClass}>
        <p>Archeologische waarde</p>
        <RefinementList attribute="arch_monuments" />
      </div>
      <div className={bClass}>
        <p>NatuurNetwerkNederland</p>
        <RefinementList attribute="nnn" />
      </div>
      <div className={bClass}>
        <p>Natura 2000</p>
        <RefinementList attribute="natura_2000" />
      </div>
    </Block>
    <Block title="Ontwikkeling">
      <div className={bClass}>
        <p>Bebouwde kom</p>
        <RefinementList attribute="built_up_area" />
        {/*<ToggleRefinement label="Binnen bebouwde kom" attribute="built_up_area" />*/}
      </div>
    </Block>
    {/*<Block title="Markt en Ontwikkelpotentie"></Block>*/}
  </BaseFacet>
);
